/* BRS engraving options: opt-in, per-type fields, validation, and the fee line item.
   Inputs live outside the <form> and are associated with it via the form attribute; disabled inputs
   are not submitted. When engraving is on, this element takes over the submit: it posts the product
   form (including any uploaded file), then adds the engraving fee product(s) with the same properties,
   then hands the last response to the theme's cart notification/drawer. */
if (!customElements.get('brs-engraving')) {
  customElements.define('brs-engraving', class extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.form);
      if (!this.form) return;
      try { this.fees = JSON.parse(this.dataset.fees || '{}'); } catch (e) { this.fees = {}; }
      this.fields = this.querySelector('.brs-engraving__fields');
      this.typeSelect = this.querySelector('[data-engraving="type"]');
      this.error = this.querySelector('.brs-engraving__error');
      this.on = false;

      this.querySelectorAll('.brs-engraving__toggle input[type=radio]').forEach((r) => {
        r.addEventListener('change', () => this.setOn(r.value === 'Yes' && r.checked));
      });
      this.typeSelect.addEventListener('change', () => this.setType());
      this.querySelectorAll('[data-logo]').forEach((r) => r.addEventListener('change', () => this.updateLogoNote()));
      this.fields.addEventListener('input', () => this.hideError());
      this.fields.addEventListener('change', (e) => { this.hideError(); if (e.target.type === 'file') this.checkFile(e.target); });

      // Capture at the document so this runs before the theme's product-form submit handler.
      this.submitHandler = (e) => this.onSubmit(e);
      document.addEventListener('submit', this.submitHandler, true);
      // Browsers restore radio state on refresh and back/forward, sometimes AFTER this runs.
      // Sync now, on the next frames, on load, and on pageshow (bfcache).
      const yes = this.querySelector('.brs-engraving__toggle input[value="Yes"]');
      const sync = () => { const on = !!(yes && yes.checked); if (on !== this.on) this.setOn(on); };
      this.setOn(!!(yes && yes.checked));
      requestAnimationFrame(sync);
      setTimeout(sync, 0);
      setTimeout(sync, 250);
      window.addEventListener('load', sync);
      window.addEventListener('pageshow', sync);
    }

    disconnectedCallback() { document.removeEventListener('submit', this.submitHandler, true); }

    setOn(on) {
      this.on = on;
      this.fields.hidden = !on;
      this.fields.querySelectorAll('input, select').forEach((el) => { el.disabled = true; });
      if (on) {
        this.fields.querySelector('input[type=hidden]').disabled = false;
        this.typeSelect.disabled = false;
        this.fields.querySelector('input[type=tel]').disabled = false;
        this.setType();
      }
      this.hideError();
    }

    currentType() {
      const opt = this.typeSelect.options[this.typeSelect.selectedIndex];
      return opt ? (opt.dataset.type || '') : '';
    }

    setType() {
      const t = this.currentType();
      this.querySelectorAll('[data-engraving-for]').forEach((g) => {
        const show = !!t && g.dataset.engravingFor.split(' ').includes(t);
        g.hidden = !show;
        g.querySelectorAll('input, select').forEach((el) => { el.disabled = !show; });
      });
      this.updateLogoNote();
    }

    updateLogoNote() {
      const note = this.querySelector('[data-logo-note]');
      const none = this.querySelector('[data-logo="none"]');
      if (note) note.hidden = !(none && none.checked && !none.disabled);
    }

    showError(msg, focusEl) {
      this.error.textContent = msg;
      this.error.hidden = false;
      if (focusEl && focusEl.focus) focusEl.focus();
      this.error.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }

    hideError() { if (this.error) this.error.hidden = true; }

    checkFile(input) {
      const f = input.files && input.files[0];
      if (!f) return true;
      const ok = /\.(jpe?g|png|heic|svg|pdf)$/i.test(f.name);
      const maxMb = parseFloat(input.dataset.maxMb || '20');
      if (!ok) { input.value = ''; this.showError('That file type will not work. Use JPG, PNG, HEIC, SVG or PDF.', input); return false; }
      if (f.size > maxMb * 1024 * 1024) { input.value = ''; this.showError(`That file is over ${maxMb} MB. Please send a smaller one.`, input); return false; }
      return true;
    }

    validate() {
      if (!this.currentType()) return this.showError('Choose an engraving type, or set Ring engraving to No.', this.typeSelect), false;
      const fileInput = this.fields.querySelector('input[type=file]');
      if (fileInput && !fileInput.disabled && !this.checkFile(fileInput)) return false;
      const groups = {};
      for (const el of this.fields.querySelectorAll('input, select')) {
        if (el.disabled || el.type === 'hidden' || el.type === 'file') continue;
        if (el.type === 'radio') { (groups[el.name] = groups[el.name] || []).push(el); continue; }
        if (el.required && !el.value.trim()) {
          const label = this.fields.querySelector(`label[for="${el.id}"]`);
          return this.showError(`Please fill in ${label ? label.textContent.trim().toLowerCase() : 'every engraving field'}.`, el), false;
        }
      }
      for (const name in groups) {
        if (!groups[name].some((r) => r.checked)) {
          const legend = groups[name][0].closest('fieldset').querySelector('legend');
          return this.showError(`Please answer: ${legend ? legend.textContent.trim() : name}`, groups[name][0]), false;
        }
      }
      return true;
    }

    properties() {
      const props = {};
      for (const el of this.fields.querySelectorAll('input, select')) {
        if (el.disabled || el.type === 'file') continue;
        if (el.type === 'radio' && !el.checked) continue;
        const m = el.name.match(/^properties\[(.+)\]$/);
        if (m && el.value) props[m[1]] = el.value;
      }
      return props;
    }

    variantLabel() {
      const parts = [];
      document.querySelectorAll('variant-selects fieldset, variant-selects .product-form__input--dropdown').forEach((grp) => {
        const legend = grp.querySelector('legend, label.form__label');
        const name = legend ? legend.childNodes[0].textContent.trim() : '';
        const picked = grp.querySelector('input[type=radio]:checked') || grp.querySelector('select');
        const label = picked && picked.value ? (name ? `${name} ${picked.value}` : picked.value) : '';
        if (label && !parts.includes(label)) parts.push(label);
      });
      return [this.dataset.product, parts.join(', ')].filter(Boolean).join(', ');
    }

    feeItems(quantity) {
      const t = this.currentType();
      const items = [];
      const add = (key, qty) => { const f = this.fees[key]; if (f && f.id) items.push({ id: f.id, quantity: qty || quantity }); };
      if (t === 'text') add('text');
      if (t === 'handwriting') add('handwriting');
      if (t === 'fingerprint') add('fingerprint');
      if (t === 'logo') {
        const status = this.querySelector('[data-logo]:checked');
        const s = status ? status.dataset.logo : '';
        if (s === 'engrave' || s === 'setup') add('logo');
        if (s === 'setup') add('logo_setup', 1); // one setup per logo, not per ring
      }
      const props = Object.assign({ 'Engraved on': this.variantLabel() }, this.properties());
      delete props['Ring Engraving'];
      return items.map((it) => Object.assign(it, { properties: props }));
    }

    async onSubmit(e) {
      if (e.target !== this.form || !this.on) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      if (!this.validate()) return;

      const submit = this.form.querySelector('[type="submit"]');
      const spinner = submit && submit.querySelector('.loading__spinner');
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const sectionIds = cart && cart.getSectionsToRender ? cart.getSectionsToRender().map((s) => s.id) : [];
      const headers = { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' };
      if (submit) { submit.setAttribute('aria-disabled', 'true'); submit.classList.add('loading'); }
      if (spinner) spinner.classList.remove('hidden');

      try {
        const fd = new FormData(this.form);
        const qty = parseInt(fd.get('quantity') || '1', 10) || 1;
        const fees = this.feeItems(qty);
        let feeKeys = [];
        if (fees.length) {
          const r1 = await fetch(window.routes.cart_add_url, { method: 'POST', headers: Object.assign({ 'Content-Type': 'application/json' }, headers), body: JSON.stringify({ items: fees }) });
          const d1 = await r1.json();
          if (d1.status) throw new Error(d1.description || d1.message || 'Could not add the engraving fee');
          feeKeys = (d1.items || []).map((it) => it.key);
        }
        // the blank goes last: its response carries the line key the cart popup renders from, and the file upload rides in the FormData
        if (sectionIds.length) { fd.append('sections', sectionIds.join(',')); fd.append('sections_url', window.location.pathname); }
        const r2 = await fetch(window.routes.cart_add_url, { method: 'POST', headers, body: fd });
        const data = await r2.json();
        if (data.status) {
          for (const key of feeKeys) { await fetch(window.routes.cart_change_url, { method: 'POST', headers: Object.assign({ 'Content-Type': 'application/json' }, headers), body: JSON.stringify({ id: key, quantity: 0 }) }).catch(() => {}); }
          throw new Error(data.description || data.message || 'Could not add to cart');
        }
        if (window.publish && window.PUB_SUB_EVENTS) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: fd.get('id'), cartData: data });
        }
        if (cart && cart.renderContents) {
          cart.renderContents(data);
        } else {
          window.location.href = window.routes.cart_url;
        }
      } catch (err) {
        this.showError(err.message || 'Something went wrong adding this to the cart. Please try again.');
      } finally {
        if (submit) { submit.removeAttribute('aria-disabled'); submit.classList.remove('loading'); }
        if (spinner) spinner.classList.add('hidden');
      }
    }
  });
}
